import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { ArrowRight, Phone, CheckCircle2 } from 'lucide-react';
import { getCompanyInfo } from '../api/api';
import { useAppearance } from '../contexts/AppearanceContext';

export const Hero = () => {
  const [company, setCompany] = useState(null);
  const { appearance } = useAppearance();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const data = await getCompanyInfo();
        setCompany(data);
      } catch (error) {
        console.error('Error fetching company info:', error);
      }
    };

    fetchCompany();
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const heroImage = appearance.hero_image || "https://images.pexels.com/photos/6197123/pexels-photo-6197123.jpeg?auto=compress&cs=tinysrgb&w=1920";
  const defaultTagline = "30 ans de métier au service des Liégeois";
  const defaultDescription = "Entreprise de nettoyage professionnelle pour particuliers et entreprises à Liège. Service rapide, fiable et de haute qualité.";

  return (
    <section id="hero" className="relative min-h-screen flex items-center pt-20 overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img
          width="1200"
          height="800"
          fetchPriority="high"
          src={heroImage}
          alt="Professional cleaning service"
          className="w-full h-full object-cover"
        />
        {/* Dégradé sombre et discret : le visuel respire, le texte reste lisible. */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-950/85 via-gray-900/60 to-gray-900/25"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="max-w-3xl">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-md border border-white/25 rounded-full px-5 py-2 mb-8 animate-fadeIn">
            <CheckCircle2 className="w-4 h-4 text-white" aria-hidden="true" />
            <span className="font-semibold text-sm text-white">30 ans de métier</span>
          </div>

          {/* Main Title */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white mb-6 leading-[1.1] animate-fadeIn">
            {company?.name ? `${company.name} – ${company.tagline}` : `NETTO+ – ${defaultTagline}`}
          </h1>

          {/* Subtitle */}
          <p className="text-lg sm:text-xl text-gray-200 mb-10 leading-relaxed max-w-xl animate-fadeIn">
            {company?.description || defaultDescription}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 animate-fadeIn">
            <Button
              onClick={() => navigate('/devis')}
              size="lg"
              className="bg-white text-gray-900 hover:bg-gray-100 font-semibold text-base px-8 py-6 rounded-xl shadow-lg transition-colors group"
            >
              Demander un devis
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
            </Button>
            <Button
              onClick={() => scrollToSection('contact')}
              size="lg"
              variant="outline"
              className="bg-transparent border border-white/40 text-white hover:bg-white/10 hover:text-white font-semibold text-base px-8 py-6 rounded-xl transition-colors"
            >
              <Phone className="mr-2 w-5 h-5" />
              Nous contacter
            </Button>
          </div>

          {/* Chiffres de confiance : renseignés depuis le panel. Un champ vide
              masque l'élément — pas de chiffre inventé par défaut. */}
          {(() => {
            const chiffres = [
              { valeur: company?.stat_years, libelle: 'Ans de métier' },
              { valeur: company?.stat_clients, libelle: 'Clients satisfaits' },
              { valeur: company?.stat_response, libelle: 'Réponse au devis' },
            ].filter((c) => c.valeur);

            if (chiffres.length === 0) return null;

            return (
              <div
                className={`grid gap-6 mt-16 pt-12 border-t border-white/20 ${
                  { 1: 'grid-cols-1', 2: 'grid-cols-2', 3: 'grid-cols-3' }[chiffres.length]
                }`}
              >
                {chiffres.map((c) => (
                  <div key={c.libelle} className="text-center">
                    <div className="text-3xl font-bold text-white mb-1">{c.valeur}</div>
                    <div className="text-sm text-blue-200">{c.libelle}</div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      </div>

      {/* Decorative Elements */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent z-10"></div>
    </section>
  );
};
